import { useState, useCallback, useRef, useEffect } from 'react';

const STORAGE_KEY = 'motrusse_sound';

// ── Sortie audio maintenue « chaude » ──────────────────────────────────────
// Sur mobile, quand aucun son n'a été émis depuis un moment, l'OS met en
// veille la sortie audio (DAC / haut-parleur / lien Bluetooth). Le play()
// suivant doit la réveiller (100-500 ms), et ce laps de temps « mord » le
// début du son — d'autant que nos mp3 (gTTS/edge-tts) démarrent à t=0, sans
// silence de tête : c'est la première syllabe qui saute.
// Parade : garder un AudioContext actif qui émet un flux 100 % silencieux.
// La route audio physique ne se rendort plus, donc plus de réveil à froid.
// Singleton au niveau module (pas par instance de hook) : un seul contexte
// même si le hook est remonté (StrictMode, navigation…).
let warmCtx = null;
let warmNode = null;

function ensureWarm() {
  const Ctx = typeof window !== 'undefined'
    && (window.AudioContext || window.webkitAudioContext);
  if (!Ctx) return; // navigateur sans WebAudio : on ne réchauffe pas, rien ne casse
  try {
    if (!warmCtx) warmCtx = new Ctx();
    if (warmCtx.state === 'suspended') warmCtx.resume();
    if (!warmNode) {
      // 1 s de silence pur (des zéros) rebouclé : inaudible, mais ça suffit à
      // garder le flux de sortie ouvert.
      const buf = warmCtx.createBuffer(1, warmCtx.sampleRate, warmCtx.sampleRate);
      const src = warmCtx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      src.connect(warmCtx.destination);
      src.start();
      warmNode = src;
    }
  } catch {}
}

export default function useAudio() {
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) !== 'off'; }
    catch { return true; }
  });
  const audioRef = useRef(null);
  // Miroir de soundEnabled lisible depuis un callback stable (playAuto).
  const soundRef = useRef(soundEnabled);
  soundRef.current = soundEnabled;

  // Onglet masqué / téléphone verrouillé / navigateur quitté : on coupe
  // l'audio en cours et play() refuse d'en démarrer un nouveau (le quiz
  // continue de tourner en arrière-plan et déclencherait des lectures).
  useEffect(() => {
    const stop = () => {
      if (audioRef.current) audioRef.current.pause();
      // En arrière-plan, on endort aussi la sortie chaude (batterie).
      if (warmCtx && warmCtx.state === 'running') warmCtx.suspend();
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else ensureWarm(); // de retour : on réchauffe avant la prochaine lecture
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', stop);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', stop);
    };
  }, []);

  // L'autoplay policy interdit de démarrer un AudioContext sans geste
  // utilisateur : on réchauffe au 1er tap/clic/touche, puis on se retire.
  useEffect(() => {
    const EVENTS = ['pointerdown', 'keydown', 'touchend'];
    const warmOnce = () => {
      ensureWarm();
      if (warmCtx && warmCtx.state === 'running') {
        EVENTS.forEach((ev) => document.removeEventListener(ev, warmOnce));
      }
    };
    EVENTS.forEach((ev) => document.addEventListener(ev, warmOnce));
    return () => EVENTS.forEach((ev) => document.removeEventListener(ev, warmOnce));
  }, []);

  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off'); } catch {}
      if (!next && audioRef.current) audioRef.current.pause();
      return next;
    });
  }, []);

  // Lecture EXPLICITE (clic sur « Écouter ») : elle joue même son coupé, car le
  // bouton est justement la façon d'écouter ponctuellement.
  const play = useCallback((url) => {
    if (!url || document.hidden) return;
    ensureWarm(); // la sortie est peut-être froide (1er clic) : on la réveille
    try {
      if (audioRef.current) audioRef.current.pause();
      const a = new Audio(url);
      audioRef.current = a;
      a.play().catch(() => {});
    } catch {}
  }, []);

  // Coupe net ce qui joue. Indispensable au changement de carte : les exercices de
  // PRODUCTION (tuiles, texte à trou, sens inverse) ne lancent volontairement aucun
  // audio à l'affichage — entendre le mot donnerait la réponse. Du coup, quand la
  // carte précédente avait lancé une lecture (la prononciation jouée à la réussite
  // des tuiles, une phrase d'exemple), plus rien ne la remplaçait : elle continuait
  // par-dessus la carte suivante. On voyait « dîner » et on entendait « manger ».
  const stop = useCallback(() => {
    if (audioRef.current) audioRef.current.pause();
  }, []);

  // Lecture AUTOMATIQUE (nouvelle question, carte retournée, anagramme résolue) :
  // elle doit obéir au réglage. Sans ce point d'entrée séparé, chaque appelant
  // devait penser à tester soundEnabled, et le bonus anagramme l'avait oublié.
  const playAuto = useCallback((url) => {
    if (!soundRef.current) return;
    play(url);
  }, [play]);

  // Appelle `cb` quand l'audio en cours se termine — ou TOUT DE SUITE si rien ne
  // joue. Sert à ne pas couper une phrase d'exemple en cours quand l'avance auto
  // du quiz arrive à échéance. Cap de sécurité (`maxWaitMs`) pour ne jamais bloquer
  // la progression si l'audio ne se termine pas (erreur réseau, onglet masqué…).
  const whenAudioIdle = useCallback((cb, maxWaitMs = 6000) => {
    const a = audioRef.current;
    const playing = a && !a.paused && !a.ended;
    if (!playing) { cb(); return; }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      a.removeEventListener('ended', finish);
      a.removeEventListener('pause', finish);
      cb();
    };
    const timer = setTimeout(finish, maxWaitMs);
    a.addEventListener('ended', finish);
    a.addEventListener('pause', finish);
  }, []);

  return { soundEnabled, toggleSound, play, playAuto, whenAudioIdle, stopAudio: stop };
}
